/**
 * PC-v2.2-C1 — «EVOLUCIÓN» EN LA CALIDAD DEL CLIENTE (G1 cumplimiento, G2 OT, G3 incidencias, G4 NC, G6 por centro)
 *
 * Un bloque secundario y plegado: Calidad no se convierte en un cuadro de mando. Sólo pide los datos al abrirse
 * (`GET /api/portal/evolucion`), con el mismo centro y periodo que el resto de la pestaña, y no pinta nada de otro
 * centro o periodo mientras llega la respuesta nueva. Qué gráficos se ven lo decide la configuración del portal
 * (catálogo de PC-v2.2-A: cada gráfico es un indicador más, que además depende de las cifras que enseña).
 *
 * Las cifras ya llegan calculadas con las mismas definiciones que Inicio y Calidad; aquí sólo se dibujan. Las OT se
 * agrupan por FECHA PREVISTA y se cuentan con su situación de HOY: no es una fotografía de cómo estaban entonces.
 *
 * PC-v2.2-C2.1: los mismos gráficos (`Contenido`) sirven a «Seguimiento del servicio» de la Calidad interna
 * (`calidad-seguimiento.tsx`), con los datos de `/api/calidad/evolucion`. Lo que cambia es sólo el texto (quién mira:
 * «que puedes ver» / «visibles para el cliente») y el G6, que en interno lleva el cliente de cada centro y puede
 * agruparse por cliente.
 */
import type { ReactNode } from 'react';

import { api, type EvolucionPortal, type RestoG6, type TramoEvolucionPortal } from '../api.ts';
import { Cargando, Error as ErrorCaja, fecha, useDatos } from '../componentes/base.tsx';
import { BarrasComparadas, GraficoColumnas, MarcoGrafico, type CategoriaGrafico } from '../componentes/graficos.tsx';
import { IND } from '../componentes/indicadores-portal.ts';

const MES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const COLOR = {
  previstas: '#0f6ea8',
  realizadas: '#167854',
  verificadas: '#0f4d36',
  incidencias: '#9a6008',
  nc: '#a5292b',
  cerradas: '#167854',
} as const;

/** Los ids del bloque, en el orden en que se pintan. */
export const IDS_EVOLUCION = [IND.calidad.evolCumplimiento, IND.calidad.evolOt, IND.calidad.evolIncidencias, IND.calidad.ncEvolucion, IND.calidad.evolPorCentro] as const;

function categoria(t: TramoEvolucionPortal, g: EvolucionPortal['granularidad']): CategoriaGrafico {
  const [a, m, d] = t.desde.split('-').map(Number) as [number, number, number];
  if (g === 'mes') {
    return {
      clave: t.clave,
      etiqueta: `${MES[m - 1]!} ${String(a).slice(2)}`,
      larga: `${MES_LARGO[m - 1]!} de ${String(a)}${t.parcial ? ` (del ${fecha(t.desde)} al ${fecha(t.hasta)})` : ''}`,
    };
  }
  return {
    clave: t.clave,
    etiqueta: `${String(d)} ${MES[m - 1]!}`,
    larga: `Semana del ${fecha(t.desde)} al ${fecha(t.hasta)}${t.parcial ? ' (parcial)' : ''}`,
  };
}

const pct = (v: number): string => `${String(v).replace('.', ',')} %`;
const n = (v: number | null | undefined): string => (v === null || v === undefined ? 'Sin datos' : String(v));

export function BloqueEvolucion({ desde, hasta, idCentro, visibles, abierto, alCambiar }: {
  desde: string;
  hasta: string;
  idCentro: string;
  visibles: ReadonlySet<string>;
  abierto: boolean;
  alCambiar: (abierto: boolean) => void;
}): ReactNode {
  const ids = IDS_EVOLUCION.filter((id) => visibles.has(id) && (id !== IND.calidad.evolPorCentro || idCentro === ''));
  if (ids.length === 0) return null;
  return (
    <details className="panel portal-evolucion" open={abierto} onToggle={(e) => { if (e.currentTarget.open !== abierto) alCambiar(e.currentTarget.open); }}>
      <summary className="panel-cabecera"><h2 style={{ margin: 0 }}>Evolución</h2><span className="pequena tenue">{ids.length} gráfico(s)</span></summary>
      {abierto && <Graficos desde={desde} hasta={hasta} idCentro={idCentro} ids={new Set(ids)} />}
    </details>
  );
}

function Graficos({ desde, hasta, idCentro, ids }: { desde: string; hasta: string; idCentro: string; ids: ReadonlySet<string> }): ReactNode {
  const q = new URLSearchParams({ desde, hasta });
  if (idCentro !== '') q.set('idCentro', idCentro);
  const d = useDatos(() => api.get<EvolucionPortal>(`/api/portal/evolucion?${q.toString()}`), [q.toString()]);
  //  Nunca la serie de otro centro o de otro periodo mientras llega la nueva.
  const e = d.datos !== null && !d.cargando && d.datos.periodo.desde === desde && d.datos.periodo.hasta === hasta
    && (d.datos.idCentro ?? '') === idCentro ? d.datos : null;
  return (
    <div className="panel-cuerpo">
      {d.error !== null && <ErrorCaja error={d.error} reintentar={d.recargar} />}
      {e === null && d.error === null && <Cargando texto="Calculando la evolución…" />}
      {e !== null && <Contenido e={e} ids={ids} perspectiva="portal" g6={e.porCentro === null ? null : {
        columna: 'Centro', filas: e.porCentro.map((c) => ({ clave: c.idCentro, nombre: c.centro, incidencias: c.incidencias, nc: c.nc })), resto: null,
      }} />}
    </div>
  );
}

/** G6 ya preparado: una fila por centro (o por cliente), con nombre único y, si hay más de 50, el resto sumado. */
export interface DatosG6 {
  columna: 'Centro' | 'Cliente';
  filas: { clave: string; nombre: string; incidencias: number; nc: number }[];
  resto: RestoG6 | null;
}
/** Quién mira: cambia el texto de las explicaciones, nunca el cálculo. */
export type Perspectiva = 'portal' | 'interna';

export function Contenido({ e, ids, perspectiva, g6, selectorG6 }: {
  e: Omit<EvolucionPortal, 'porCentro'>;
  ids: ReadonlySet<string>;
  perspectiva: Perspectiva;
  g6: DatosG6 | null;
  /** Interno: el control para agrupar G6 por cliente o por centro. */
  selectorG6?: ReactNode;
}): ReactNode {
  const interna = perspectiva === 'interna';
  const unidad = e.granularidad === 'semana' ? 'semana' : 'mes';
  const cats = e.tramos.map((t) => categoria(t, e.granularidad));
  const conSinDatos = e.tramos.some((t) => !t.conDatos);
  const conDatos = e.tramos.filter((t) => t.conDatos).length;
  const pocos = conDatos > 0 && conDatos < 3
    ? `Sólo hay ${String(conDatos)} ${conDatos === 1 ? unidad : unidad === 'mes' ? 'meses' : 'semanas'} con datos: todavía no indica una tendencia.`
    : undefined;
  const filaTramo = (k: number): ReactNode => <th scope="row">{cats[k]!.larga}</th>;
  const cumpl = e.tramos.map((t) => t.ot?.cumplimiento.pct ?? null);
  return (
    <>
      <p className="pequena tenue" style={{ marginTop: 0 }}>
        Del {fecha(e.periodo.desde)} al {fecha(e.periodo.hasta)}, por {e.granularidad === 'semana' ? 'semanas (de lunes a domingo)' : 'meses'}.
        {' '}{e.datosDesde === null ? 'Todavía no hay datos en este ámbito.' : `Datos desde el ${fecha(e.datosDesde)}.`}
      </p>
      <div className="portal-evolucion-rejilla">
        {ids.has(IND.calidad.evolCumplimiento) && (
          <MarcoGrafico
            titulo="Evolución del cumplimiento"
            explicacion={<>De las órdenes previstas en cada {unidad}, las realizadas sobre las que ya debían estar hechas (realizadas, no realizadas y fuera de plazo), según su situación de hoy. Sin nada que debiera estar hecho: «Sin datos», nunca 0 %.</>}
            nota={pocos ?? (cats.length > 8 ? 'Las órdenes detrás de cada porcentaje están en «Ver como tabla».' : undefined)}
            tabla={(
              <table className="tabla-simple" aria-label="Evolución del cumplimiento">
                <thead><tr><th>{e.granularidad === 'semana' ? 'Semana' : 'Mes'}</th><th>Cumplimiento</th><th>Realizadas</th><th>Debían estar hechas</th><th>Previstas</th></tr></thead>
                <tbody>
                  {e.tramos.map((t, k) => (
                    <tr key={t.clave}>
                      {filaTramo(k)}
                      <td>{t.ot === null || t.ot.cumplimiento.pct === null ? 'Sin datos' : pct(t.ot.cumplimiento.pct)}</td>
                      <td>{n(t.ot?.cumplimiento.n)}</td><td>{n(t.ot?.cumplimiento.total)}</td><td>{n(t.ot?.previstas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          >
            <GraficoColumnas
              etiqueta={`Cumplimiento por ${unidad}: ${e.tramos.map((t, k) => `${cats[k]!.larga}, ${cumpl[k] === null ? 'sin datos' : `${pct(cumpl[k]!)} (${String(t.ot!.cumplimiento.n)} de ${String(t.ot!.cumplimiento.total)})`}`).join('; ')}`}
              //  Las OT detrás de cada porcentaje bajo su columna mientras caben (hasta 8 tramos); siempre en la tabla.
              categorias={cats.length > 8 ? cats : cats.map((c, k) => ({ ...c, etiqueta: cumpl[k] === null ? c.etiqueta : `${c.etiqueta}\n${String(e.tramos[k]!.ot!.cumplimiento.n)}/${String(e.tramos[k]!.ot!.cumplimiento.total)}` }))}
              series={[{ clave: 'cumplimiento', nombre: 'Cumplimiento', valores: cumpl, color: COLOR.realizadas, trama: 'lisa' }]}
              maximo={100} formato={pct} conSinDatos={cumpl.some((v) => v === null)}
            />
          </MarcoGrafico>
        )}
        {ids.has(IND.calidad.evolOt) && (
          <MarcoGrafico
            titulo="Previstas, realizadas y verificadas"
            explicacion={<>Órdenes previstas en cada {unidad} y cuántas de ellas están hoy realizadas y verificadas. Las verificadas son parte de las realizadas: las columnas no se suman.</>}
            nota={pocos}
            tabla={(
              <table className="tabla-simple" aria-label="Previstas, realizadas y verificadas">
                <thead><tr><th>{e.granularidad === 'semana' ? 'Semana' : 'Mes'}</th><th>Previstas</th><th>Realizadas</th><th>Verificadas</th><th>No realizadas</th><th>Fuera de plazo</th></tr></thead>
                <tbody>
                  {e.tramos.map((t, k) => (
                    <tr key={t.clave}>
                      {filaTramo(k)}
                      <td>{n(t.ot?.previstas)}</td><td>{n(t.ot?.realizadas)}</td><td>{n(t.ot?.verificadas)}</td>
                      <td>{n(t.ot?.noRealizadas)}</td><td>{n(t.ot?.vencidas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          >
            <GraficoColumnas
              etiqueta={`Órdenes por ${unidad}: ${e.tramos.map((t, k) => `${cats[k]!.larga}, ${t.ot === null ? 'sin datos' : `${String(t.ot.previstas)} previstas, ${String(t.ot.realizadas)} realizadas, ${String(t.ot.verificadas)} verificadas`}`).join('; ')}`}
              categorias={cats}
              series={[
                { clave: 'previstas', nombre: 'Previstas', valores: e.tramos.map((t) => t.ot?.previstas ?? null), color: COLOR.previstas, trama: 'lisa' },
                { clave: 'realizadas', nombre: 'Realizadas', valores: e.tramos.map((t) => t.ot?.realizadas ?? null), color: COLOR.realizadas, trama: 'rayas' },
                { clave: 'verificadas', nombre: 'Verificadas', valores: e.tramos.map((t) => t.ot?.verificadas ?? null), color: COLOR.verificadas, trama: 'puntos' },
              ]}
              conSinDatos={conSinDatos}
            />
          </MarcoGrafico>
        )}
        {ids.has(IND.calidad.evolIncidencias) && e.tramos.some((t) => !t.conDatos || t.incidencias !== null) && (
          <MarcoGrafico
            titulo="Evolución de incidencias"
            explicacion={<>Incidencias {interna ? 'visibles para el cliente' : 'que puedes ver'}, registradas y resueltas en cada {unidad} por la fecha de cada hecho: una registrada en un{unidad === 'semana' ? 'a semana' : ' mes'} y resuelta en otr{unidad === 'semana' ? 'a' : 'o'} cuenta en los dos.</>}
            nota={pocos}
            tabla={(
              <table className="tabla-simple" aria-label="Evolución de incidencias">
                <thead><tr><th>{e.granularidad === 'semana' ? 'Semana' : 'Mes'}</th><th>Registradas</th><th>Resueltas</th></tr></thead>
                <tbody>
                  {e.tramos.map((t, k) => <tr key={t.clave}>{filaTramo(k)}<td>{n(t.incidencias?.registradas)}</td><td>{n(t.incidencias?.resueltas)}</td></tr>)}
                </tbody>
              </table>
            )}
          >
            <GraficoColumnas
              etiqueta={`Incidencias por ${unidad}: ${e.tramos.map((t, k) => `${cats[k]!.larga}, ${t.incidencias === null ? 'sin datos' : `${String(t.incidencias.registradas)} registradas, ${String(t.incidencias.resueltas)} resueltas`}`).join('; ')}`}
              categorias={cats}
              series={[
                { clave: 'registradas', nombre: 'Registradas', valores: e.tramos.map((t) => t.incidencias?.registradas ?? null), color: COLOR.incidencias, trama: 'lisa' },
                { clave: 'resueltas', nombre: 'Resueltas', valores: e.tramos.map((t) => t.incidencias?.resueltas ?? null), color: COLOR.realizadas, trama: 'rayas' },
              ]}
              conSinDatos={conSinDatos}
            />
          </MarcoGrafico>
        )}
        {ids.has(IND.calidad.ncEvolucion) && (
          <MarcoGrafico
            titulo="Evolución de no conformidades"
            explicacion={<>No conformidades {interna ? 'que ve el cliente (visibles o abiertas por él)' : 'que puedes ver'}, abiertas y cerradas en cada {unidad} (por su fecha de apertura y de cierre).</>}
            nota={pocos}
            tabla={(
              <table className="tabla-simple" aria-label="Evolución de no conformidades">
                <thead><tr><th>{e.granularidad === 'semana' ? 'Semana' : 'Mes'}</th><th>Abiertas</th><th>Cerradas</th></tr></thead>
                <tbody>
                  {e.tramos.map((t, k) => <tr key={t.clave}>{filaTramo(k)}<td>{n(t.nc?.registradas)}</td><td>{n(t.nc?.cerradas)}</td></tr>)}
                </tbody>
              </table>
            )}
          >
            <GraficoColumnas
              etiqueta={`No conformidades por ${unidad}: ${e.tramos.map((t, k) => `${cats[k]!.larga}, ${t.nc === null ? 'sin datos' : `${String(t.nc.registradas)} abiertas, ${String(t.nc.cerradas)} cerradas`}`).join('; ')}`}
              categorias={cats}
              series={[
                { clave: 'abiertas', nombre: 'Abiertas', valores: e.tramos.map((t) => t.nc?.registradas ?? null), color: COLOR.nc, trama: 'lisa' },
                { clave: 'cerradas', nombre: 'Cerradas', valores: e.tramos.map((t) => t.nc?.cerradas ?? null), color: COLOR.cerradas, trama: 'rayas' },
              ]}
              conSinDatos={conSinDatos}
            />
          </MarcoGrafico>
        )}
        {ids.has(IND.calidad.evolPorCentro) && g6 !== null && (
          <MarcoGrafico
            titulo={g6.columna === 'Cliente' ? 'Incidencias y no conformidades por cliente' : 'Incidencias y no conformidades por centro'}
            explicacion={interna
              ? <>Visibles para el cliente y registradas del {fecha(e.periodo.desde)} al {fecha(e.periodo.hasta)}, {g6.columna === 'Cliente' ? 'sumando los centros de cada cliente a los que tienes acceso' : 'en cada centro (con su cliente)'}.</>
              : <>Registradas del {fecha(e.periodo.desde)} al {fecha(e.periodo.hasta)} en cada uno de tus centros.</>}
            nota={g6.resto === null ? undefined : `Se muestran ${String(g6.filas.length)}; ${textoResto(g6)}`}
            tabla={(
              <table className="tabla-simple" aria-label={g6.columna === 'Cliente' ? 'Incidencias y no conformidades por cliente' : 'Incidencias y no conformidades por centro'}>
                <thead><tr><th>{g6.columna}</th><th>Incidencias</th><th>No conformidades</th></tr></thead>
                <tbody>
                  {g6.filas.map((c) => <tr key={c.clave}><th scope="row">{c.nombre}</th><td>{c.incidencias}</td><td>{c.nc}</td></tr>)}
                  {g6.resto !== null && <tr><th scope="row">{`Resto (${String(g6.resto.grupos)})`}</th><td>{g6.resto.incidencias}</td><td>{g6.resto.nc}</td></tr>}
                </tbody>
              </table>
            )}
          >
            {selectorG6}
            {g6.filas.length === 0 ? <p className="pequena tenue">No hay centros activos.</p> : (
              <BarrasComparadas
                etiqueta={`Por ${g6.columna === 'Cliente' ? 'cliente' : 'centro'}: ${g6.filas.map((c) => `${c.nombre}, ${String(c.incidencias)} incidencias y ${String(c.nc)} no conformidades`).join('; ')}`}
                filas={g6.filas.map((c) => ({ clave: c.clave, nombre: c.nombre, valores: [c.incidencias, c.nc] }))}
                series={[
                  { clave: 'incidencias', nombre: 'Incidencias', color: COLOR.incidencias, trama: 'lisa' },
                  { clave: 'nc', nombre: 'No conformidades', color: COLOR.nc, trama: 'rayas' },
                ]}
              />
            )}
          </MarcoGrafico>
        )}
      </div>
    </>
  );
}

function textoResto(g6: DatosG6): string {
  const r = g6.resto!;
  const que = g6.columna === 'Cliente' ? `${String(r.grupos)} cliente(s) más` : `${String(r.grupos)} centro(s) más`;
  return `${que}, con ${String(r.incidencias)} incidencia(s) y ${String(r.nc)} no conformidad(es), en la fila «Resto» de la tabla.`;
}
